import { useEffect, useMemo } from 'react';
import { Box, Card, CardContent, Grid, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import CoreReportsService from '../../../services/CoreReportsService';
import { ChartCard, Legend, LineChart, SERIES_COLORS, StatTile } from './ReportCharts';
import { compact, count, formatDay, formatSize, periodLabel } from './reportsFormat';
import useReport from './useReport';
import ReportState from './ReportState';

const shardName = (s) => s.name || `Shard ${s.shardId}`;

export default function InfraTab({ days, onFreshness }) {
  const params = useMemo(() => ({ days }), [days]);
  const report = useReport((p) => CoreReportsService.infra(p), params);
  const data = report.data;
  const freshness = data?.freshness || null;
  useEffect(() => { onFreshness?.(freshness); }, [freshness, onFreshness]);

  return <ReportState report={report}>{data && <Content data={data} />}</ReportState>;
}

function Content({ data }) {
  const { shards, topCompaniesBySize: top } = data;
  const dates = useMemo(() => [...new Set(shards.flatMap((s) => s.salesCountByDay.map((d) => d.date)))].sort(), [shards]);
  const plotted = shards.slice(0, SERIES_COLORS.length);
  const series = plotted.map((s, i) => {
    const byDate = new Map(s.salesCountByDay.map((d) => [d.date, d.salesCount]));
    return { name: shardName(s), color: SERIES_COLORS[i], values: dates.map((d) => byDate.get(d) || 0) };
  });
  const labels = dates.map((d) => periodLabel(d, 'day'));
  const maxSize = Math.max(1, ...top.map((t) => Number(t.dbSizeMb)));

  return (
    <Box>
      <Grid container spacing={2}>
        {shards.map((s) => (
          <Grid item xs={12} md={6} key={s.shardId}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{shardName(s)}</Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>{s.host || 'Sin fila en db_shard'}</Typography>
                <Grid container spacing={2} sx={{ mt: 0 }}>
                  <Grid item xs={6} sm={4}><StatTile label="Empresas" value={count(s.companies)} /></Grid>
                  <Grid item xs={6} sm={4}><StatTile label="Tamaño total" value={formatSize(s.dbSizeMbTotal)} /></Grid>
                  <Grid item xs={6} sm={4}><StatTile label="Ventas del período" value={count(s.salesCount)} hint={`${count(s.ecommerceOrdersCount)} pedidos online`} /></Grid>
                  <Grid item xs={6} sm={4}><StatTile label="Usuarios (staff)" value={count(s.staffTotal)} /></Grid>
                  <Grid item xs={6} sm={4}><StatTile label="Clientes" value={count(s.clientsTotal)} /></Grid>
                  <Grid item xs={6} sm={4}>
                    <StatTile
                      label="Requests a la API" value={s.apiRequests === null || s.apiRequests === undefined ? 'Sin medir' : compact(s.apiRequests)}
                      hint={s.apiErrors5xx === null || s.apiErrors5xx === undefined ? 'Falta el contador por empresa' : `${count(s.apiErrors5xx)} errores 5xx`}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        ))}
        {shards.length === 0 && <Grid item xs={12}><Typography sx={{ color: 'text.secondary' }}>Todavía no hay datos de infraestructura.</Typography></Grid>}
      </Grid>

      <Box sx={{ mt: 2 }}>
        <ChartCard
          title="Ventas por día y por base de datos" subtitle="Cantidad de ventas procesadas por cada shard" empty={dates.length === 0 ? 'Todavía no hay datos para este rango.' : null}
          legend={series.length > 1 ? <Legend items={series.map((s) => ({ name: s.name, color: s.color }))} /> : null}
          table={{ columns: ['Día', ...series.map((s) => s.name)], rows: dates.map((d, i) => [formatDay(d), ...series.map((s) => count(s.values[i]))]) }}
        >
          <LineChart labels={labels} series={series} format={(v) => compact(v)} />
        </ChartCard>
        {shards.length > SERIES_COLORS.length && (
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>El gráfico muestra los primeros {SERIES_COLORS.length} shards; el resto está en las tarjetas de arriba.</Typography>
        )}
      </Box>

      <Card variant="outlined" sx={{ mt: 2 }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Empresas más grandes</Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>Tamaño del schema de cada empresa según la última foto</Typography>
          <Table size="small" sx={{ mt: 1 }}>
            <TableHead><TableRow><TableCell>Empresa</TableCell><TableCell align="right">Shard</TableCell><TableCell sx={{ width: '40%' }}>Tamaño</TableCell></TableRow></TableHead>
            <TableBody>
              {top.map((t) => (
                <TableRow key={t.id}>
                  <TableCell>{t.name}</TableCell>
                  <TableCell align="right">{t.shardId}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ height: 8, width: `${Math.max(2, (Number(t.dbSizeMb) / maxSize) * 100)}%`, maxWidth: '70%', bgcolor: SERIES_COLORS[0], borderRadius: '0 4px 4px 0' }} />
                      <Typography variant="caption">{formatSize(t.dbSizeMb)}</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
              {top.length === 0 && <TableRow><TableCell colSpan={3} align="center" sx={{ color: 'text.secondary', py: 3 }}>Sin datos.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </Box>
  );
}
